<template>
  <div class="process-design-container">
    <!-- 顶部工具栏 -->
    <div class="design-toolbar">
      <el-button type="primary" icon="el-icon-back" @click="handleBack">返回</el-button>
      <el-button icon="el-icon-view" @click="handlePreview">预览</el-button>
      <!-- <el-button icon="el-icon-download" @click="handleExport">导出</el-button>
      <el-button icon="el-icon-upload2" @click="handleImport">导入</el-button> -->
      <el-button type="success" icon="el-icon-check" @click="handleSave">保存</el-button>
      <el-button type="warning" icon="el-icon-switch-button" @click="handleValidate">验证</el-button>

      <div class="toolbar-right">
        <el-tag type="info">当前流程：{{ processInfo.definition_name }}</el-tag>
        <!-- <el-tag>版本：v{{ processInfo.version }}</el-tag> -->
      </div>
    </div>

    <!-- 设计区域 -->
    <div class="design-main">
      <!-- 左侧节点库 -->
      <div class="design-sidebar">
        <div class="node-library">
          <h3>节点库</h3>
          <div class="node-list">
            <div
              v-for="node in nodeTypes"
              :key="node.type"
              class="node-item"
              :draggable="true"
              @dragstart="handleDragStart($event, node)"
              :data-type="node.type"
            >
              <i :class="node.icon"></i>
              <span>{{ node.name }}</span>
            </div>
          </div>
        </div>

        <div class="process-properties">
          <h3>流程属性</h3>
          <el-form label-position="top" size="small">
              <el-form-item label="流程名称" class="full-width-form-item process-name-item">
                <el-input v-model="processInfo.definition_name" style="margin-left: 0 !important;" />
              </el-form-item>
            </el-form>
        </div>

        <!-- 节点配置面板 -->
        <div class="node-config-panel" v-if="selectedNode">
          <h3>节点配置</h3>
          <NodeConfig
            :node="selectedNode"
            :process-id="processInfo.id"
            :all-nodes="nodes"
            @update="handleNodeUpdate"
            @delete="handleNodeDelete"
          />
        </div>
      </div>

      <!-- 中间画布区域 -->
      <div
        class="design-canvas"
        @drop="handleDrop"
        @dragover="handleDragOver"
        ref="canvasRef"
      >
        <div class="canvas-grid"></div>

        <!-- 流程节点 -->
        <div
          v-for="node in nodes"
          :key="node.id"
          class="process-node"
          :style="{ left: node.position_x + 'px', top: node.position_y + 'px' }"
          :class="[
            `node-type-${node.node_type}`,
            { 'node-selected': selectedNode && selectedNode.id === node.id, 'node-dragging': draggingNode && draggingNode.id === node.id }
          ]"
          @click="handleNodeSelect(node)"
          @contextmenu.prevent="handleNodeContextMenu($event, node)"
          @mousedown="handleNodeMouseDown($event, node)"
          draggable="false"
        >
          <div class="node-header">
            <i :class="getNodeIcon(node.node_type)"></i>
            <span class="node-name">{{ node.node_name }}</span>
          </div>
          <div class="node-content">
            <div class="node-no">{{ node.node_no }}</div>
            <div class="node-handler" v-if="node.handler_type === 1">
              角色：{{ getRoleNames(node.handler_role_ids) }}
            </div>
            <div class="node-handler" v-else-if="node.handler_type === 2">
              用户：{{ getUserNames(node.handler_user_ids) }}
            </div>
            <!-- 调试信息 -->
            <!-- <div class="node-debug" style="font-size: 10px; color: #999;">
              handler_type: {{ node.handler_type }},
              roleIds: {{ Array.isArray(node.handler_role_ids) ? node.handler_role_ids.length : 'N/A' }}
            </div> -->
          </div>
        </div>

        <!-- 连线 -->
        <svg class="connection-layer">
          <path
            v-for="(conn, index) in connections"
            :key="index"
            :d="conn.path"
            class="connection-line"
            marker-end="url(#arrowhead)"
          />
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7"
                    refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" />
            </marker>
          </defs>
        </svg>
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      title="流程预览"
      :visible.sync="previewVisible"
      width="90%"
      top="5vh"
    >
      <ProcessPreview :process-data="processInfo" :nodes="nodes" />
    </el-dialog>

    <!-- 节点右键菜单 -->
    <div
      v-if="contextMenu.visible"
      class="context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
    >
      <div class="menu-item" @click="handleEditNode">
        <i class="el-icon-edit"></i>编辑节点
      </div>
      <div class="menu-item" @click="handleDeleteNode">
        <i class="el-icon-delete"></i>删除节点
      </div>
      <div class="menu-item" @click="handleCopyNode">
        <i class="el-icon-copy-document"></i>复制节点
      </div>
      <div class="menu-divider"></div>
      <div class="menu-item" @click="handleSetStartNode">
        <i class="el-icon-position"></i>设为开始节点
      </div>
      <div class="menu-item" @click="handleSetEndNode">
        <i class="el-icon-finished"></i>设为结束节点
      </div>
    </div>
  </div>
</template>

<script>
import NodeConfig from './components/NodeConfig.vue'
import ProcessPreview from './components/ProcessPreview.vue'
import {
  apiProcessNodeList,
  apiProcessNodeBatchSave,
  apiProcessDefinitionDetail
} from '@/api/service'

export default {
  name: 'ProcessDesign',
  components: { NodeConfig, ProcessPreview },

  data () {
    return {
      processInfo: {
        id: 0,
        definition_name: '',
        approval_type: 1
      },

      nodes: [],
      selectedNode: null,
      connections: [],

      nodeTypes: [
        { type: 1, name: '开始节点', icon: 'el-icon-caret-right' },
        { type: 2, name: '审批节点', icon: 'el-icon-s-check' },
        { type: 3, name: '执行节点', icon: 'el-icon-s-operation' },
        { type: 4, name: '结束节点', icon: 'el-icon-switch-button' }
      ],

      previewVisible: false,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        node: null
      },

      isDragging: false,
      draggingNode: null,
      dragOffset: { x: 0, y: 0 }
    }
  },

  computed: {
    definitionId () {
      return this.$route.query.id || 0
    }
  },

  mounted () {
    this.loadProcessData()
    document.addEventListener('click', this.handleDocumentClick)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mouseup', this.handleMouseUp)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleDocumentClick)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
  },

  methods: {
    // 加载流程数据
    async loadProcessData () {
      if (!this.definitionId) return

      try {
        // 加载流程定义
        const res = await apiProcessDefinitionDetail({ id: this.definitionId })
        // console.log('res',res)
        this.processInfo = res

        // 加载节点数据
        const data = await apiProcessNodeList({ definition_id: this.definitionId })
        // console.log('data',data)
        this.nodes = data.lists || []

        // 处理节点数据，确保格式正确
        this.nodes.forEach(node => {
          // 确保每个节点都有action_type字段
          if (node.action_type === undefined || node.action_type === null) {
            node.action_type = 1 // 默认为审批
          }

          // 确保每个节点都有handler_type字段
          if (node.handler_type === undefined || node.handler_type === null) {
            node.handler_type = 1 // 默认为角色处理
          }

          // 解析handler_role_ids，如果是JSON字符串则转换为数组
          if (node.handler_role_ids && typeof node.handler_role_ids === 'string') {
            try {
              node.handler_role_ids = JSON.parse(node.handler_role_ids)
            } catch (e) {
              console.error('解析handler_role_ids失败:', e)
              node.handler_role_ids = []
            }
          } else if (!node.handler_role_ids) {
            node.handler_role_ids = []
          }

          // 解析handler_user_ids，如果是JSON字符串则转换为数组
          if (node.handler_user_ids && typeof node.handler_user_ids === 'string') {
            try {
              node.handler_user_ids = JSON.parse(node.handler_user_ids)
            } catch (e) {
              console.error('解析handler_user_ids失败:', e)
              node.handler_user_ids = []
            }
          } else if (!node.handler_user_ids) {
            node.handler_user_ids = []
          }

          // 确保位置信息是数字类型
          if (node.position_x !== undefined && node.position_x !== null) {
            node.position_x = Number(node.position_x)
          }
          if (node.position_y !== undefined && node.position_y !== null) {
            node.position_y = Number(node.position_y)
          }
        })

        // 仅在节点没有位置信息时才自动布局
        this.autoLayoutNodesIfNoPosition()

        // 生成连线
        this.generateConnections()
      } catch (error) {
        this.$message.error('加载流程数据失败')
      }
    },

    // 生成节点连线
    generateConnections () {
      this.connections = []

      this.nodes.forEach(node => {
        if (node.next_node_id) {
          const targetNode = this.nodes.find(n => n.id === node.next_node_id)
          if (targetNode) {
            const path = this.calculatePath(node, targetNode)
            this.connections.push({
              from: node.id,
              to: targetNode.id,
              path
            })
          }
        }
      })
    },

    // 计算连线路径
    calculatePath (fromNode, toNode) {
      // 节点尺寸
      const nodeWidth = 200
      const nodeHeight = 80 // 使用最小高度作为基准

      // 计算节点中心点
      const x1 = fromNode.position_x + nodeWidth / 2
      const y1 = fromNode.position_y + nodeHeight / 2
      const x2 = toNode.position_x + nodeWidth / 2
      const y2 = toNode.position_y + nodeHeight / 2

      // 简单的直线路径
      return `M ${x1} ${y1} L ${x2} ${y2}`
    },

    // 获取节点图标
    getNodeIcon (nodeType) {
      const iconMap = {
        1: 'el-icon-caret-right',
        2: 'el-icon-s-check',
        3: 'el-icon-s-operation',
        4: 'el-icon-switch-button'
      }
      return iconMap[nodeType] || 'el-icon-question'
    },

    // 获取角色名称
    getRoleNames (roleIds) {
      if (!roleIds) return '无'

      // 如果是数组，直接使用
      if (Array.isArray(roleIds)) {
        return roleIds.length ? `${roleIds.length}个角色` : '无'
      }

      // 如果是字符串，尝试解析为数组
      try {
        const ids = JSON.parse(roleIds)
        return ids.length ? `${ids.length}个角色` : '无'
      } catch (e) {
        console.error('解析handler_role_ids失败:', e)
        return '无'
      }
    },

    // 获取用户名称
    getUserNames (userIds) {
      if (!userIds) return '无'

      // 如果是数组，直接使用
      if (Array.isArray(userIds)) {
        return userIds.length ? `${userIds.length}个用户` : '无'
      }

      // 如果是字符串，尝试解析为数组
      try {
        const ids = JSON.parse(userIds)
        return ids.length ? `${ids.length}个用户` : '无'
      } catch (e) {
        console.error('解析handler_user_ids失败:', e)
        return '无'
      }
    },

    // 节点拖拽开始
    handleDragStart (event, node) {
      event.dataTransfer.setData('nodeType', node.type)
      this.isDragging = true
    },

    // 节点鼠标按下事件（用于拖动节点）
    handleNodeMouseDown (event, node) {
      // 阻止默认行为和冒泡
      event.preventDefault()
      event.stopPropagation()

      // 设置拖动状态
      this.draggingNode = node

      // 计算鼠标相对于节点的偏移量
      const canvasRect = this.$refs.canvasRef.getBoundingClientRect()
      this.dragOffset = {
        x: event.clientX - canvasRect.left - node.position_x,
        y: event.clientY - canvasRect.top - node.position_y
      }

      // 添加全局样式，防止文本选中
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'move'
    },

    // 鼠标移动事件
    handleMouseMove (event) {
      if (!this.draggingNode) return

      // 获取画布位置
      const canvasRect = this.$refs.canvasRef.getBoundingClientRect()

      // 计算新位置
      let newX = event.clientX - canvasRect.left - this.dragOffset.x
      let newY = event.clientY - canvasRect.top - this.dragOffset.y

      // 限制在画布范围内
      newX = Math.max(0, Math.min(newX, canvasRect.width - 200)) // 节点宽度为200px
      newY = Math.max(0, Math.min(newY, canvasRect.height - 80)) // 节点最小高度为80px

      // 更新节点位置
      this.draggingNode.position_x = newX
      this.draggingNode.position_y = newY

      // 更新连线
      this.generateConnections()
    },

    // 鼠标释放事件
    handleMouseUp () {
      if (!this.draggingNode) return

      // 重置拖动状态
      this.draggingNode = null

      // 恢复全局样式
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    },

    // 拖拽到画布
    handleDragOver (event) {
      event.preventDefault()
    },

    // 在画布上释放
    handleDrop (event) {
      event.preventDefault()
      const nodeType = event.dataTransfer.getData('nodeType')
      if (!nodeType) return

      const canvasRect = this.$refs.canvasRef.getBoundingClientRect()
      const x = event.clientX - canvasRect.left - 50
      const y = event.clientY - canvasRect.top - 40

      this.createNode(nodeType, x, y)
    },

    // 创建新节点
    createNode (nodeType, x, y) {
      // 生成较短的ID，避免数据库溢出
      const newId = this.generateShortId()

      const newNode = {
        id: newId,
        definition_id: this.definitionId,
        node_no: `NODE_${newId}`,
        node_name: '新节点',
        node_type: parseInt(nodeType),
        handler_type: 1,
        handler_role_ids: '[]',
        handler_user_ids: '[]',
        action_type: 1,
        require_signature: 0,
        require_photos: 0,
        require_attachments: 0,
        require_payment: 0,
        payment_type: null,
        payment_percentage: 0,
        payment_amount: 0,
        require_quotation: 0,
        require_warranty_check: 0,
        time_limit_hours: null,
        next_node_id: null,
        branch_conditions: null,
        parallel_nodes: null,
        position_x: x,
        position_y: y,
        sort: this.nodes.length + 1,
        remark: ''
      }

      this.nodes.push(newNode)
      this.selectedNode = newNode

      // 如果是结束节点，自动设置为前面节点的下一节点
      if (newNode.node_type === 4) {
        const lastNode = this.nodes.find(n => n.id !== newNode.id && n.node_type !== 4 && !n.next_node_id)
        if (lastNode) {
          lastNode.next_node_id = newNode.id
        }
      }

      // 自动重新布局（仅当没有其他节点有位置信息时）
      this.autoLayoutNodesIfNoPosition()
    },

    // 生成较短的ID
    generateShortId () {
      // 使用时间戳的后6位 + 3位随机数，确保ID在10位数以内
      const timestamp = Date.now().toString().slice(-6)
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      return parseInt(timestamp + random)
    },

    // 节点选择
    handleNodeSelect (node) {
      // 创建节点对象的深拷贝，确保数据格式正确
      const processedNode = JSON.parse(JSON.stringify(node))

      // 确保handler_role_ids和handler_user_ids是数组格式
      if (processedNode.handler_role_ids && typeof processedNode.handler_role_ids === 'string') {
        try {
          processedNode.handler_role_ids = JSON.parse(processedNode.handler_role_ids)
        } catch (e) {
          console.error('解析handler_role_ids失败:', e)
          processedNode.handler_role_ids = []
        }
      } else if (!processedNode.handler_role_ids) {
        processedNode.handler_role_ids = []
      }

      if (processedNode.handler_user_ids && typeof processedNode.handler_user_ids === 'string') {
        try {
          processedNode.handler_user_ids = JSON.parse(processedNode.handler_user_ids)
        } catch (e) {
          console.error('解析handler_user_ids失败:', e)
          processedNode.handler_user_ids = []
        }
      } else if (!processedNode.handler_user_ids) {
        processedNode.handler_user_ids = []
      }

      // 确保所有必要字段都存在，特别是action_type和require_quotation
      if (processedNode.action_type === undefined || processedNode.action_type === null) {
        processedNode.action_type = 1
      }

      if (processedNode.require_quotation === undefined || processedNode.require_quotation === null) {
        processedNode.require_quotation = 0
      }

      // 确保handler_type字段存在
      if (processedNode.handler_type === undefined || processedNode.handler_type === null) {
        processedNode.handler_type = 1
      }

      console.log('处理后的节点数据:', processedNode)
      this.selectedNode = processedNode
      this.contextMenu.visible = false
    },

    // 节点右键菜单
    handleNodeContextMenu (event, node) {
      event.preventDefault()
      this.contextMenu = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        node
      }
      this.selectedNode = node
    },

    // 文档点击（隐藏右键菜单）
    handleDocumentClick () {
      this.contextMenu.visible = false
    },

    // 节点右键菜单操作
    handleEditNode () {
      // 编辑节点逻辑
      this.$message.info('编辑节点')
    },

    async handleDeleteNode () {
      try {
        await this.$confirm('确定删除该节点吗？', '提示', {
          type: 'warning'
        })

        // 从nodes数组中移除
        const index = this.nodes.findIndex(n => n.id === this.contextMenu.node.id)
        if (index > -1) {
          this.nodes.splice(index, 1)
          this.selectedNode = null

          // 自动重新布局（仅当没有其他节点有位置信息时）
          this.autoLayoutNodesIfNoPosition()

          this.$message.success('节点已删除')
        }
      } catch {
        // 取消删除
      }
    },

    handleCopyNode () {
      const node = this.contextMenu.node
      // 生成较短的ID，避免数据库溢出
      const newId = this.generateShortId()

      const newNode = {
        ...node,
        id: newId,
        node_no: `${node.node_no}_COPY`,
        node_name: `${node.node_name}_副本`,
        position_x: node.position_x + 50,
        position_y: node.position_y + 50
      }
      this.nodes.push(newNode)
      this.selectedNode = newNode
    },

    handleSetStartNode () {
      this.processInfo.start_node_id = this.contextMenu.node.id
      this.$message.success('已设置为开始节点')
    },

    handleSetEndNode () {
      // 设置结束节点逻辑
      this.$message.info('设为结束节点')
    },

    // 节点更新
    handleNodeUpdate (updatedNode) {
      const index = this.nodes.findIndex(n => n.id === updatedNode.id)
      if (index > -1) {
        // 保留原有节点的位置信息和允许操作
        const originalNode = this.nodes[index]

        // 确保handler_role_ids和handler_user_ids是数组格式
        if (typeof updatedNode.handler_role_ids === 'string') {
          try {
            updatedNode.handler_role_ids = JSON.parse(updatedNode.handler_role_ids)
          } catch (e) {
            console.error('解析handler_role_ids失败:', e)
            updatedNode.handler_role_ids = []
          }
        }

        if (typeof updatedNode.handler_user_ids === 'string') {
          try {
            updatedNode.handler_user_ids = JSON.parse(updatedNode.handler_user_ids)
          } catch (e) {
            console.error('解析handler_user_ids失败:', e)
            updatedNode.handler_user_ids = []
          }
        }

        // 保留位置信息
        updatedNode.position_x = originalNode.position_x
        updatedNode.position_y = originalNode.position_y

        // 如果表单中没有提供allow_actions，则保留原有值
        if (!updatedNode.allow_actions) {
          updatedNode.allow_actions = originalNode.allow_actions
        }

        this.nodes.splice(index, 1, updatedNode)
        this.generateConnections()
      }
    },

    // 从属性面板删除节点
    handleNodeDelete (nodeId) {
      const index = this.nodes.findIndex(n => n.id === nodeId)
      if (index > -1) {
        this.nodes.splice(index, 1)
        this.selectedNode = null

        // 自动重新布局（仅当没有其他节点有位置信息时）
        this.autoLayoutNodesIfNoPosition()

        this.$message.success('节点已删除')
      }
    },

    // 保存流程
    async handleSave () {
      try {
        // 验证流程
        if (!await this.validateProcess()) return

        // 使用批量保存API
        await apiProcessNodeBatchSave({
          definition_id: this.definitionId,
          nodes: this.nodes.map(node => ({
            id: node.id || 0,
            node_type: node.node_type,
            node_name: node.node_name,
            node_description: node.node_description || '',
            sort: node.sort,
            is_required: node.is_required ? 1 : 0,
            action_type: node.action_type || 1, // 添加操作类型字段
            handler_type: node.handler_type || 1,
            // 确保角色和用户ID以数组形式保存，而不是JSON字符串
            handler_role_ids: Array.isArray(node.handler_role_ids)
              ? node.handler_role_ids
              : (typeof node.handler_role_ids === 'string' ? JSON.parse(node.handler_role_ids || '[]') : []),
            handler_user_ids: Array.isArray(node.handler_user_ids)
              ? node.handler_user_ids
              : (typeof node.handler_user_ids === 'string' ? JSON.parse(node.handler_user_ids || '[]') : []),
            require_quotation: node.require_quotation !== undefined ? node.require_quotation : 0, // 添加需要报价字段
            branch_conditions: node.branch_conditions ? JSON.stringify(node.branch_conditions) : null,
            parallel_nodes: node.parallel_nodes ? JSON.stringify(node.parallel_nodes) : null,
            next_node_id: node.next_node_id || null,
            // 添加允许操作字段，确保是数组格式
            allow_actions: Array.isArray(node.allow_actions)
              ? node.allow_actions
              : (typeof node.allow_actions === 'string' ? JSON.parse(node.allow_actions || '[]') : []),
            // 添加节点位置信息
            position_x: node.position_x !== undefined && node.position_x !== null ? node.position_x : 0,
            position_y: node.position_y !== undefined && node.position_y !== null ? node.position_y : 0
          }))
        })
        // this.$message.success('流程保存成功')
      } catch (error) {
        // this.$message.error('保存失败：' + (error.message || '未知错误'))
      }
    },

    // 验证流程
    async handleValidate () {
      if (await this.validateProcess()) {
        this.$message.success('流程验证通过')
      }
    },

    // 流程验证逻辑
    async validateProcess () {
      // 检查是否有开始节点
      const startNodes = this.nodes.filter(n => n.node_type === 1)
      if (startNodes.length === 0) {
        this.$message.error('请添加开始节点')
        return false
      }

      if (startNodes.length > 1) {
        this.$message.error('只能有一个开始节点')
        return false
      }

      // 检查是否有结束节点
      const endNodes = this.nodes.filter(n => n.node_type === 4)
      if (endNodes.length === 0) {
        this.$message.error('请添加结束节点')
        return false
      }

      // 检查节点连接
      for (const node of this.nodes) {
        if (node.node_type !== 1 && node.node_type !== 4 && !node.next_node_id) { // 开始节点和结束节点不需要next_node_id
          this.$message.error(`节点 "${node.node_name}" 未设置下一节点`)
          return false
        }
      }

      // 检查是否有孤立节点
      const allNodeIds = new Set(this.nodes.map(n => n.id))
      const connectedNodeIds = new Set()

      // 从开始节点开始遍历
      const startNode = startNodes[0]
      this.traverseNode(startNode.id, connectedNodeIds)

      const isolatedNodes = this.nodes.filter(n => !connectedNodeIds.has(n.id))
      if (isolatedNodes.length > 0) {
        this.$message.error(`存在孤立节点：${isolatedNodes.map(n => n.node_name).join(', ')}`)
        return false
      }

      return true
    },

    // 遍历节点连接
    traverseNode (nodeId, visited) {
      if (visited.has(nodeId)) return
      visited.add(nodeId)

      const node = this.nodes.find(n => n.id === nodeId)
      if (node && node.next_node_id) {
        this.traverseNode(node.next_node_id, visited)
      }
    },

    // 预览流程
    handlePreview () {
      this.previewVisible = true
    },

    // 导出流程
    handleExport () {
      // 导出逻辑
      this.$message.info('导出功能开发中')
    },

    // 导入流程
    handleImport () {
      // 导入逻辑
      this.$message.info('导入功能开发中')
    },

    // 自动布局节点，防止重叠
    autoLayoutNodes () {
      if (!this.nodes || this.nodes.length === 0) return

      // 节点尺寸和间距
      const nodeWidth = 220
      const nodeHeight = 120
      const horizontalSpacing = 50
      const verticalSpacing = 80

      // 按节点类型分组
      const startNodes = this.nodes.filter(n => n.node_type === 1)
      const endNodes = this.nodes.filter(n => n.node_type === 6 || n.node_type === 4)
      const processNodes = this.nodes.filter(n => n.node_type !== 1 && n.node_type !== 6 && n.node_type !== 4)

      // 布局开始节点
      startNodes.forEach((node, index) => {
        node.position_x = 50
        node.position_y = 100 + index * (nodeHeight + verticalSpacing)
      })

      // 布局处理节点（最多3列）
      processNodes.forEach((node, index) => {
        const column = index % 3 + 1
        const row = Math.floor(index / 3)

        node.position_x = 50 + column * (nodeWidth + horizontalSpacing)
        node.position_y = 100 + row * (nodeHeight + verticalSpacing)
      })

      // 布局结束节点
      endNodes.forEach((node, index) => {
        node.position_x = 50 + 3 * (nodeWidth + horizontalSpacing) // 从4列改为3列，使结束节点不要太靠右
        node.position_y = 100 + index * (nodeHeight + verticalSpacing)
      })

      // 自动设置下一节点
      this.autoSetNextNodes()

      // 更新连线
      this.$nextTick(() => {
        this.generateConnections()
      })
    },

    // 仅在节点没有位置信息时才自动布局
    autoLayoutNodesIfNoPosition () {
      if (!this.nodes || this.nodes.length === 0) return

      // 检查是否所有节点都有明确的位置信息（不为undefined或null）
      const hasPositionInfo = this.nodes.every(node =>
        node.position_x !== undefined &&
        node.position_y !== undefined &&
        node.position_x !== null &&
        node.position_y !== null
      )

      // 如果所有节点都有位置信息，则不进行自动布局
      if (hasPositionInfo) {
        // 确保节点有有效的下一节点关系
        this.autoSetNextNodes()
        return
      }

      // 否则进行自动布局
      this.autoLayoutNodes()
    },

    // 自动设置下一节点
    autoSetNextNodes () {
      // 按节点类型分组
      const startNodes = this.nodes.filter(n => n.node_type === 1)
      const processNodes = this.nodes.filter(n => n.node_type !== 1 && n.node_type !== 6 && n.node_type !== 4)
      const endNodes = this.nodes.filter(n => n.node_type === 6 || n.node_type === 4)

      // 为开始节点设置下一节点
      startNodes.forEach(startNode => {
        if (!startNode.next_node_id && processNodes.length > 0) {
          startNode.next_node_id = processNodes[0].id
        }
      })

      // 为处理节点设置下一节点
      processNodes.forEach((node, index) => {
        if (!node.next_node_id) {
          // 如果是最后一个处理节点，连接到结束节点
          if (index === processNodes.length - 1 && endNodes.length > 0) {
            node.next_node_id = endNodes[0].id
          }
          // 否则连接到下一个处理节点
          else if (index < processNodes.length - 1) {
            node.next_node_id = processNodes[index + 1].id
          }
        }
      })

      // 结束节点不需要设置下一节点
    },

    // 返回列表
    handleBack () {
      this.$router.push('/work_order/process/list')
    }
  }
}
</script>

<style lang="scss" scoped>

.process-design-container {
  height: 120vh;
  display: flex;
  flex-direction: column;

  .design-toolbar {
    padding: 10px 20px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    align-items: center;
    gap: 10px;

    .toolbar-right {
      margin-left: auto;
      display: flex;
      gap: 10px;
    }
  }

  .design-main {
    flex: 1;
    display: flex;
    overflow: hidden;

    .design-sidebar {
      width: 300px;
      background: #f5f7fa;
      border-right: 1px solid #e4e7ed;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .node-library {
        padding: 15px;
        border-bottom: 1px solid #e4e7ed;

        h3 {
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #303133;
        }

        .node-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;

          .node-item {
            width: 110px;
            height: 60px;
            border: 1px dashed #c0c4cc;
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: move;
            background: #fff;
            transition: all 0.3s;

            &:hover {
              border-color: #409eff;
              box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
            }

            i {
              font-size: 20px;
              margin-bottom: 5px;
            }

            span {
              font-size: 12px;
              color: #606266;
            }
          }
        }
      }

      .process-properties {
        padding: 15px;
        flex: none;
        height: auto;
        min-height: 0;
        overflow-y: visible;

        h3 {
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #303133;
        }

        .full-width-form-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        // 全局覆盖Element UI的表单项样式
        :deep(.el-form-item__content) {
          margin-left: 0 !important;
        }

        // 特别针对流程名称的样式
        .process-name-item {
          :deep(.el-form-item__content) {
            margin-left: 0 !important;
            padding-left: 0 !important;
            text-align: left;
          }
        }

        // 更强制性的样式覆盖
        .process-sidebar .el-form .el-form-item .el-form-item__content {
          margin-left: 0 !important;
        }
      }

      .node-config-panel {
        padding: 15px;
        flex: 1;
        overflow-y: auto;
        border-top: 1px solid #e4e7ed;
        min-height: 0; // 确保flex子元素可以缩小
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;

        h3 {
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        // 确保节点配置组件不会超出宽度
        ::v-deep .node-config {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
        }
      }
    }

    .design-canvas {
      flex: 1;
      position: relative;
      background: #fff;
      overflow: auto;
      max-width: 1400px;
      max-height: 800px;
      margin: 0 auto;

      .canvas-grid {
        position: absolute;
        width: 1400px;
        height: 800px;
        background-image:
          linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 0, 0, 0.1) 1px, transparent 1px);
        background-size: 20px 20px;
      }

      .process-node {
        position: absolute;
        width: 200px;
        min-height: 80px;
        border: 2px solid #dcdfe6;
        border-radius: 8px;
        background: #fff;
        cursor: move;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transition: all 0.3s;

        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 12px 0 rgba(32, 160, 255, 0.2);
        }

        &.node-selected {
          border-color: #409eff;
          box-shadow: 0 2px 12px 0 rgba(32, 160, 255, 0.3);
        }

        &.node-dragging {
          opacity: 0.8;
          z-index: 1000;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          cursor: grabbing;
        }

        .node-header {
          padding: 8px 12px;
          background: #f5f7fa;
          border-bottom: 1px solid #e4e7ed;
          display: flex;
          align-items: center;
          gap: 8px;
          border-radius: 6px 6px 0 0;

          i {
            color: #409eff;
          }

          .node-name {
            font-weight: 500;
            color: #303133;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .node-content {
          padding: 10px 12px;
          font-size: 12px;
          color: #606266;

          .node-no {
            color: #909399;
            margin-bottom: 5px;
          }

          .node-handler {
            background: #f0f9ff;
            padding: 4px 8px;
            border-radius: 4px;
            margin-top: 5px;
            border: 1px solid #d9ecff;
          }
        }
      }

      .connection-layer {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;

        .connection-line {
          fill: none;
          stroke: #c0c4cc;
          stroke-width: 2;
          stroke-dasharray: 5,5;

          &:hover {
            stroke: #409eff;
          }
        }
      }
    }
  }

  .context-menu {
    position: fixed;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    z-index: 1000;

    .menu-item {
      padding: 8px 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      color: #606266;
      font-size: 14px;

      &:hover {
        background: #f5f7fa;
        color: #409eff;
      }
    }

    .menu-divider {
      height: 1px;
      background: #e4e7ed;
      margin: 5px 0;
    }
  }
}

// 节点类型颜色
.process-node.node-type-1 { border-color: #67c23a; }
.process-node.node-type-2 { border-color: #409eff; }
.process-node.node-type-3 { border-color: #e6a23c; }
.process-node.node-type-4 { border-color: #909399; }
</style>
